import { Link } from "react-router-dom";
import { ArrowUpRight, Shield, Wrench, Box, Zap } from "lucide-react";
import { HERO_IMAGES } from "@/data/products";
import { Marquee } from "@/components/Marquee";
import { SEO } from "@/components/SEO";
import { FEATURE_FLAGS } from "@/config/featureFlags";

const CATEGORIES = [
  {
    label: "01",
    title: "WELLSIDE CANOPIES",
    sub: "OEM-grade canopies for trade, fleet & overland builds",
    desc: "NZ's strongest aluminium canopies. 300kg dynamic load. Lighter than fibreglass. Bigger gullwing doors.",
    image: HERO_IMAGES.wellsideHilux,
    to: "/wellside-canopies",
  },
  {
    label: "02",
    title: "X-SERIES TRAY & CANOPY",
    sub: "Service body engineered for serious expedition use",
    desc: "500kg dynamic load. Tested in Africa. Onboard water, electrical & fitout for true overland builds.",
    image: HERO_IMAGES.xseriesDuo,
    to: "/x-series-tray",
  },
  {
    label: "03",
    title: "BUSH CABIN UTE CAMPER",
    sub: "Live-in comfort. Real 4WD capability.",
    desc: "Sub 200kg fully insulated camper. Internal sleeping access. EEZI-AWN 100mm mattress. From $24,990.",
    image: "https://www.xpeditionz.co.nz/api/files/xpeditionz/bushcabin/6a56db5ff843dd19ef0df337/b74ca77d-eb94-49f1-b9fb-263f79380ac5.jpg",
    // Bright beach photo — the shared default gradient (black/80 at the
    // bottom) looked far too heavy on this one, so it gets a lighter
    // overlay just for this card rather than dimming the other two.
    overlay: "from-black/40 via-black/5 to-transparent",
    to: "/bush-cabin",
  },
];

const PILLARS = [
  { icon: Shield, kpi: "300kg", label: "Dynamic Load Rating", note: "Tested to 2.5 tonnes static" },
  { icon: Wrench, kpi: "TIG", label: "Welded Frame", note: "Not a bolt-together kit" },
  { icon: Box, kpi: "10yr", label: "Frame Guarantee", note: "Manufacturer-backed" },
  { icon: Zap, kpi: "65kg", label: "Typical Weight", note: "Lighter than fibreglass" },
];

export default function BushtechOverviewPage() {
  return (
    <div data-testid="bushtech-overview-page">
      <SEO
        path="/bushtech"
        title="Bushtech NZ — Aluminium Canopies, Tray Systems & Ute Campers"
        description="Full Bushtech range in New Zealand — Wellside canopies (23 models), X-Series expedition tray & canopy service body, and the Bush Cabin ute camper. TIG-welded aluminium. 10-year frame guarantee."
      />
      {/* HERO */}
      <section className="relative overflow-hidden bg-[var(--bg-main)] md:min-h-[100svh] md:flex md:items-end">
        <div className="relative aspect-[4/5] md:absolute md:inset-0 md:aspect-auto">
          <img src={HERO_IMAGES.wellside} alt="Bushtech Wellside canopy on a Ford Ranger Tremor" className="w-full h-full object-cover object-[50%_0%]" />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-main)] from-0% via-black/55 via-30% to-transparent to-55% md:from-10% md:via-black/10 md:via-40%" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/15 via-black/0 to-transparent md:from-black/32 md:via-black/4 md:via-transparent" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/35 from-0% via-black/5 via-10% to-transparent to-18% pointer-events-none" />
        </div>
        <div className="relative z-10 -mt-[50%] md:mt-0 w-full">
          <div className="container-x pt-10 pb-10 md:pb-20 md:pt-32">
          <p className="overline mb-4 md:mb-6 fade-in-up">— Bushtech NZ · Premium Aluminium Ute Solutions</p>
          <h1 className="font-display text-[13vw] md:text-[9vw] lg:text-[7.5vw] leading-[0.9] text-white hero-text-shadow fade-in-up max-w-[16ch]" style={{animationDelay: "0.1s"}}>
            BUILT TO WORK.<br />
            <span className="text-[var(--brand-primary)]">BUILT TO WANDER.</span>
          </h1>
          <div className="mt-6 md:mt-10 flex flex-col md:flex-row items-start md:items-end justify-between gap-6 md:gap-8 fade-in-up" style={{animationDelay: "0.3s"}}>
            <p className="md:hidden text-base font-medium text-white/95 max-w-xl leading-relaxed [text-shadow:0_1px_3px_rgba(0,0,0,0.9),0_2px_20px_rgba(0,0,0,0.7)]">
              Genuine OEM-quality aluminium canopies, trays and ute campers. Every canopy is fully TIG-welded — <strong className="text-white font-semibold">not a cheap bolt-together kit</strong>.
            </p>
            <p className="hidden md:block text-lg text-white/80 max-w-xl leading-relaxed">
              Genuine OEM-quality aluminium ute canopies, trays and ute campers, proudly distributed in New Zealand by XpeditioNZ. Every canopy is built on a fully TIG-welded aluminium frame — <strong className="text-white font-semibold">not a cheap bolt-together kit</strong>. Engineered in South Africa, proven globally.
            </p>
            <div className="grid grid-cols-2 gap-3 w-full md:w-auto">
              <Link to="/wellside-canopies" data-testid="hero-cta-wellside" className="btn-primary justify-center text-center">Wellside Canopies <ArrowUpRight size={14} /></Link>
              {FEATURE_FLAGS.HARD_LIDS_ENABLED && (
                <Link to="/hard-lids" data-testid="hero-cta-hardlids" className="btn-secondary justify-center text-center">Hard Lids</Link>
              )}
              <Link to="/x-series-tray" data-testid="hero-cta-xseries" className="btn-secondary justify-center text-center">X-Series Tray & Canopy</Link>
              <Link to="/bush-cabin" data-testid="hero-cta-bushcabin" className="btn-secondary justify-center text-center">Bush Cabin Camper</Link>
            </div>
          </div>
          </div>
        </div>
      </section>

      <Marquee />

      {/* INTRO STATEMENT */}
      <section className="hidden md:block container-x py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <p className="overline mb-6">— The Standard</p>
            <h2 className="font-display text-4xl md:text-5xl leading-[0.95] text-white">
              A HIGHER STANDARD<br />OF OVERLAND GEAR<br /><span className="text-[var(--brand-primary)]">EXISTS.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 lg:col-start-6 space-y-6 text-[var(--text-secondary)] text-lg leading-relaxed">
            <p>
              Most aluminium ute canopies fall into two camps — low-cost bolt-together kits built to a price, or premium options at significantly higher cost. <span className="text-white">Bushtech sits in neither.</span>
            </p>
            <p>
              Built around a fully TIG-welded aluminium frame, every Bushtech canopy delivers OEM-grade construction, class-leading dust and water sealing, and a 10-year manufacturer frame guarantee. Originally engineered in South Africa for permanently off-road fleets, now trusted by NZ new-vehicle dealerships.
            </p>
            <p className="text-white">
              Lighter than fibreglass. Stronger than the rest. With bigger gullwing access doors than bolt-together canopies can support.
            </p>
          </div>
        </div>
      </section>

      {/* PILLARS BENTO */}
      <section className="container-x pb-12 md:pb-24">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--border-default)] border border-[var(--border-default)]">
          {PILLARS.map((p, i) => {
            const Icon = p.icon;
            return (
              <div key={i} data-testid={`pillar-${i}`} className="bg-[var(--bg-main)] p-8 md:p-10 group hover:bg-[var(--bg-secondary)] transition-colors">
                <Icon size={28} className="text-[var(--brand-primary)] mb-8" strokeWidth={1.2} />
                <p className="font-display text-5xl md:text-6xl text-white leading-none">{p.kpi}</p>
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--text-secondary)] mt-4">{p.label}</p>
                <p className="text-sm text-[var(--text-tertiary)] mt-2">{p.note}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="container-x pb-16 md:pb-24">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-6">
          <div>
            <p className="overline mb-4">— Product Lines</p>
            <h2 className="font-display text-4xl md:text-6xl text-white leading-[0.95]">
              THREE PLATFORMS.<br />ONE STANDARD.
            </h2>
          </div>
          <p className="text-[var(--text-secondary)] max-w-md">
            From everyday trade canopies to true expedition-grade campers — every Bushtech build shares the same welded aluminium DNA.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-[var(--border-default)] border border-[var(--border-default)]">
          {CATEGORIES.map((c, i) => (
            <Link
              key={i}
              to={c.to}
              data-testid={`category-card-${i}`}
              className="group relative block bg-[var(--bg-main)] hover:bg-[var(--bg-secondary)] transition-colors overflow-hidden"
            >
              <div className="aspect-[4/3] overflow-hidden relative">
                <img src={c.image} alt={c.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className={`absolute inset-0 bg-gradient-to-t ${c.overlay || "from-black/80 via-black/20 to-transparent"}`} />
                <span className="absolute top-5 left-5 font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--brand-primary)] bg-black/50 px-2 py-1">{c.label}</span>
              </div>
              <div className="p-8">
                <h3 className="font-display text-2xl md:text-3xl text-white leading-tight">{c.title}</h3>
                <p className="text-sm font-mono uppercase tracking-wider text-[var(--brand-primary)] mt-2">{c.sub}</p>
                <p className="text-[var(--text-secondary)] mt-4 leading-relaxed">{c.desc}</p>
                <span className="inline-flex items-center gap-2 mt-6 text-xs font-mono uppercase tracking-[0.18em] text-white group-hover:text-[var(--brand-primary)] transition-colors">
                  Explore <ArrowUpRight size={14} />
                </span>
              </div>
            </Link>
          ))}
        </div>

        {/* HARD LID CROSS-SELL — some visitors land on this canopy overview
            page actually looking for a lockable tub cover rather than a
            canopy; point them straight to the dedicated Tour Top page.
            Hidden while Hard Lids is not yet approved for release. */}
        {FEATURE_FLAGS.HARD_LIDS_ENABLED && (
        <Link
          to="/hard-lids"
          data-testid="bushtech-hardlid-crosssell"
          className="group relative grid grid-cols-1 md:grid-cols-2 border border-[var(--border-default)] hover:border-[var(--brand-primary)] transition-colors bg-[var(--bg-secondary)] overflow-hidden mt-px"
        >
          <div className="aspect-[4/3] md:aspect-auto overflow-hidden relative order-2 md:order-1">
            <img
              src="/products/hard-lids/lock-detail.webp"
              alt="Tour Top Cover hard lid, closed and locked on a ute tub, doubling as a roof rack with bikes mounted on top"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              style={{ objectPosition: "65% 30%" }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent md:hidden" />
          </div>
          <div className="p-8 md:p-12 flex flex-col justify-center order-1 md:order-2">
            <p className="overline mb-3">— Looking For Something Else?</p>
            <h3 className="font-display text-3xl md:text-4xl text-white leading-tight">
              ACTUALLY LOOKING FOR <span className="text-[var(--brand-primary)]">A HARD LID?</span>
            </h3>
            <p className="text-[var(--text-secondary)] mt-4 leading-relaxed text-lg">
              Tour Top Cover is a lockable hard lid for your ute tub that doubles as a genuine, load-rated roof rack — not a canopy.
            </p>
            <span className="inline-flex items-center gap-2 mt-6 text-sm font-mono uppercase tracking-[0.18em] text-white group-hover:text-[var(--brand-primary)] transition-colors">
              Click Here For Hard Lids <ArrowUpRight size={16} />
            </span>
          </div>
        </Link>
        )}
      </section>
    </div>
  );
}
